/**
 * The shell's word to a screen that draws its own «Sin conexión»: the browser is offline. While it is, the shell
 * neither freezes that screen nor writes its general sentence over it, and the screen shows the state its drawing has
 * (the TPV's chip with «Reintentar», its ticket that cannot open a table). Every operation is still refused offline by
 * the Identity runtime (`runtime/identity-runtime.ts`, `setInteractionFrozen`), so nothing is sent; only the page stays
 * readable and navigable. Every other screen keeps the shell's freeze and sentence.
 */
import { createContext, useContext } from 'react';

const ShellOfflineContext = createContext(false);

export const ShellOfflineProvider = ShellOfflineContext.Provider;

/** True while the browser is offline, inside a screen the registry marks `drawsOwnOffline`; false everywhere else. */
export const useShellOffline = (): boolean => useContext(ShellOfflineContext);

/**
 * What a screen that keeps working without a connection reports to the shell: while it `keepsWorking`, the shell never
 * freezes it and draws none of its general lines over it; while it `holdsReauthentication`, the reauthentication sheet
 * waits for it. The shell forgets the report when the screen changes.
 */
export type ScreenConnectivityReport = Readonly<{ keepsWorking: boolean; holdsReauthentication: boolean }>;

export const SCREEN_CONNECTED_REPORT: ScreenConnectivityReport = Object.freeze({ keepsWorking: false, holdsReauthentication: false });

const ScreenConnectivityContext = createContext<(report: ScreenConnectivityReport) => void>(() => undefined);

export const ScreenConnectivityProvider = ScreenConnectivityContext.Provider;

/** The screen's report to the shell (nothing listens outside the shell). */
export const useScreenConnectivityReport = (): ((report: ScreenConnectivityReport) => void) => useContext(ScreenConnectivityContext);
